import React, { useState, useEffect, useMemo } from "react";
import {
  FaBox, FaChartLine, FaCalendarAlt, FaClock,
  FaExclamationTriangle, FaBuilding, FaMicrochip,
  FaCoffee, FaCogs, FaPhone,
  FaCircle, FaUserAlt,
  FaEnvelope, FaMapMarkerAlt,
} from "react-icons/fa";
import { MdOutlineRemoveShoppingCart } from "react-icons/md";
import { getDeviceCupNumber, getGrinderStatus } from "../../../utils/deviceUtils";
import Drawer from "../../shared/Drawer";
import { AppLink } from "../../shared";

import turkishCoffeeMachine from "../../../Assets/icons/turkish-coffee-machine.png";
import teaFilterCoffeeMachine from "../../../Assets/icons/tea-filter-coffee-machine-icon.png";
import teaTurkishCoffeeMachine from "../../../Assets/icons/tea_coffee_machine.png";
import useDrawerTransition from "../../../Hooks/useDrawerTransition";
import { deviceActivityStateLabel } from "../../../utils/deviceActivityLabels";
import ParentFirmBadge from "../../Alerts/storageRoles/ParentFirmBadge";
import OrderResponsibleNote from "../../Alerts/storageRoles/OrderResponsibleNote";
import FirmContactsMenu from "../../Alerts/storageRoles/FirmContactsMenu";
import LastOrderBillPanel from "../../Alerts/storageRoles/LastOrderBillPanel";
import { BillReceiptIcon } from "../../atoms/animatedIcons";

// Cihaz listesindeki makine görselleriyle aynı set
const DEVICE_IMAGES = {
  Fincanda: turkishCoffeeMachine,
  Çayset: teaFilterCoffeeMachine,
  "Çayset-Fincanda": teaTurkishCoffeeMachine,
};

// Tüketim yapmayan cihaz: firmaya konma tarihi (#269 — statusChangedAt)
const formatPlacedDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" })
    : null;
const placedLabel = (device) => {
  const placed = formatPlacedDate(device.statusChangedAt);
  return placed ? `Firmaya kondu: ${placed}` : null;
};

// Kritik kotada yalnızca bu temel ürünler dikkate alınır (espresso/çikolata vb. hariç)
const BASE_PRODUCTS = ["Çay", "Türk Kahvesi", "Filtre Kahve"];

// Cihaz uyarı listelerinde kategoriye özel İKİNCİ "detay" sütunu
// (DeviceStatusDrawer ile aynı büyük yazım deseni)
const DEVICE_DETAIL_META = {
  service: { label: "Serviste", text: "text-indigo-600" },
  playmaker: { label: "Plasiyerde", text: "text-pink-600" },
  unused: { label: "Tüketim Yok", text: "text-slate-600" },
  critical: { label: "Kritik Kota", text: "text-red-600" },
};

const getFirmNavTo = (firmId) =>
  firmId ? `/Anasayfa/Firma/${firmId}` : "/Anasayfa/Firma";
const getFirmNavState = (firmId, firm) =>
  firmId
    ? { firm, fromPage: "Anasayfa/Dashboard" }
    : { searchFirmName: firm?.firmName || "", fromPage: "Anasayfa/Dashboard" };

// Son sipariş faturasını açan hareketli ikon düğmesi (#367)
function LastBillButton({ product, active, onClick, size = "md" }) {
  if (!product?.lastOrderInfo?.billNo) return null;
  const box = size === "sm" ? "h-6 w-6" : "h-8 w-8";
  const icon = size === "sm" ? "h-4 w-4" : "h-5 w-5";
  return (
    <button
      type="button"
      onClick={onClick}
      title="Son sipariş faturasını göster"
      aria-pressed={active}
      className={`inline-flex ${box} shrink-0 items-center justify-center rounded-lg border transition-colors ${
        active
          ? "border-indigo-400 bg-indigo-600 text-white"
          : "border-indigo-200 bg-white text-indigo-600 hover:border-indigo-400 hover:bg-indigo-50"
      }`}
    >
      <BillReceiptIcon className={icon} trigger={active ? "off" : "always"} />
    </button>
  );
}

const billKey = (firm, product) =>
  `${firm?._id || firm?.firmId || ""}:${product?.productName || ""}`;

export default function AlertDrawer({ alertDrawer: alertDrawerProp, onClose }) {
  // Kapanış animasyonu boyunca içerik dondurulur; drawer ekrandan çıkana
  // kadar başlık/liste değişmez.
  const { mounted, data: alertDrawer } = useDrawerTransition(
    !!alertDrawerProp,
    alertDrawerProp
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Açık son sipariş faturası (#367): { firm, product } — tablo sola daralır
  const [billTarget, setBillTarget] = useState(null);
  const toggleBill = (firm, product) =>
    setBillTarget((cur) =>
      cur && billKey(cur.firm, cur.product) === billKey(firm, product) ? null : { firm, product }
    );
  const activeBillKey = billTarget ? billKey(billTarget.firm, billTarget.product) : null;
  // Fatura paneli açıkken listede boş bir yere tıklamak paneli kapatır;
  // düğme, bağlantı ve muhatap listesi gibi etkileşimli öğeler hariç (#367)
  const closeBillOnListClick = (e) => {
    if (!billTarget) return;
    if (e.target.closest?.("button, a, input, select, textarea, [role='button'], [data-keep-bill]")) return;
    setBillTarget(null);
  };

  useEffect(() => {
    const calcPageSize = () => {
      const fixedH = 64 + 48;
      const rowH = 72;
      const available = window.innerHeight - fixedH;
      setPageSize(Math.max(10, Math.floor(available / rowH)));
    };
    calcPageSize();
    window.addEventListener("resize", calcPageSize);
    return () => window.removeEventListener("resize", calcPageSize);
  }, []);

  useEffect(() => {
    if (alertDrawer) setPage(1);
    setBillTarget(null);
  }, [alertDrawer]);

  const listCount = useMemo(() => {
    if (!alertDrawer?.data?.length) return 0;
    if (
      alertDrawer.dataType === "firm" &&
      (alertDrawer.title === "Sipariş Vermeyen Firmalar" ||
        (alertDrawer.data[0]?.productName != null &&
          (alertDrawer.data[0]?.firmId != null || alertDrawer.data[0]?._id != null)))
    ) {
      const byId = {};
      alertDrawer.data.forEach((f) => {
        const id = String(f._id ?? f.firmId ?? "");
        if (id) byId[id] = true;
      });
      return Object.keys(byId).length;
    }
    return alertDrawer.data.length;
  }, [alertDrawer]);

  if (!mounted || !alertDrawer) return null;

  const rawData = alertDrawer.data || [];
  const isNotOrderedFirms =
    alertDrawer.dataType === "firm" &&
    (alertDrawer.title === "Sipariş Vermeyen Firmalar" ||
      (rawData[0] && rawData[0].productName != null && (rawData[0].firmId != null || rawData[0]._id != null)));

  const allItems = isNotOrderedFirms
    ? (() => {
        const byId = {};
        rawData.forEach((f) => {
          const id = String(f._id ?? f.firmId ?? "");
          if (!id) return;
          if (!byId[id]) {
            byId[id] = { firmName: f.firmName, _id: f._id, firmId: f.firmId, email: f.email, tel: f.tel, address: f.address, bayserNo: f.bayserNo, group: f.group, responsible: f.responsible, contacts: f.contacts, products: [f] };
          } else {
            byId[id].products.push(f);
          }
        });
        return Object.values(byId);
      })()
    : rawData;

  // Düz liste; bayi / ana firma her satırda rozetle görünür ("Bayiye göre"
  // düğmesi kaldırıldı)
  const pageStart = (page - 1) * pageSize;
  const pageRows = allItems
    .slice(pageStart, pageStart + pageSize)
    .map((item) => ({ type: "item", item }));

  const totalPages = Math.max(1, Math.ceil(listCount / pageSize));

  // Cihaz listelerinde başlıktan kategori türet — detay sütunu buna göre
  // (Bağlanmayan'da ayrı sütun yok; gün bilgisi Bağlantı sütununda büyük yazılır)
  const drawerTitle = alertDrawer.title || "";
  const deviceDetailMode =
    alertDrawer.dataType === "device"
      ? drawerTitle.includes("Serviste")
        ? "service"
        : drawerTitle.includes("Plasiyerde")
          ? "playmaker"
          : drawerTitle.includes("Tüketim")
            ? "unused"
            : drawerTitle.toLowerCase().includes("kritik")
              ? "critical"
              : null
      : null;
  const deviceDetailMeta = deviceDetailMode ? DEVICE_DETAIL_META[deviceDetailMode] : null;
  // Gruplu görünümde başlık satırı tüm sütunları kaplar

  const titleIcon =
    alertDrawer.dataType === "device" ? (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" />
      </svg>
    ) : (
      <FaBuilding className="w-5 h-5" />
    );

  return (
    <Drawer
      // open prop'tan okunur: dondurulmuş veri kapanışta da dolu kalır; ona
      // bağlanınca Drawer kapandığını hiç görmüyor, kaymadan siliniyordu (#227)
      open={!!alertDrawerProp}
      onClose={onClose}
      title={alertDrawer.title}
      subtitle={`${listCount} kayıt listeleniyor`}
      titleIcon={titleIcon}
      width="xl"
      headerGradient
    >
      {/* Fatura paneli açıkken (#367) gövde ikiye bölünür: liste solda daralır,
          fatura detayı sağda. Mobilde panel listenin yerini alır. */}
      <Drawer.Body className={billTarget ? "bg-white !overflow-hidden flex" : "bg-white"}>
        <div
          role="presentation"
          onClick={closeBillOnListClick}
          className={
            billTarget
              ? "hidden sm:block sm:w-1/2 xl:w-[55%] min-w-0 overflow-y-auto border-r border-gray-200 transition-[width] duration-300"
              : ""
          }
        >
        {/* Mobil kart görünümü — dar ekranda tablo yerine dikey kartlar */}
        <div className="sm:hidden divide-y divide-gray-100">
          {alertDrawer.dataType === "device"
            ? pageRows.map((row, idx) => {
                const device = row.item;
                const devFirmId = device.firm?._id || device.firmId;
                const isConnected = device.deviceInfo?.connection?.status;
                const lastConn = device.deviceInfo?.connection?.lastConnected || device.lastConnectionDate;
                const offlineDays = !isConnected && lastConn
                  ? Math.floor((Date.now() - new Date(lastConn).getTime()) / 86400000)
                  : null;
                const daysInStatus = device.statusChangedAt
                  ? Math.floor((Date.now() - new Date(device.statusChangedAt).getTime()) / 86400000)
                  : null;
                const noConsDays = device.lastConsumptionDate
                  ? Math.floor((Date.now() - new Date(device.lastConsumptionDate).getTime()) / 86400000)
                  : null;
                const crits = (device.productInfo?.productInfo || []).filter(
                  (p) => p.quotaDistribution && p.quota < p.quotaWarning
                );
                const devImg = DEVICE_IMAGES[device.deviceInfo?.deviceName || device.deviceName];
                return (
                  <div key={device._id || idx} className="px-4 py-3 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <AppLink
                        to={`/Anasayfa/Cihazlar/${device._id}`}
                        state={{ device, fromPage: "Anasayfa/Dashboard" }}
                        onClick={onClose}
                        className="flex items-center gap-2.5 min-w-0"
                      >
                        <div className="w-10 h-10 shrink-0 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
                          {devImg ? (
                            <img src={devImg} alt="" className="max-w-full max-h-full object-contain p-0.5" />
                          ) : (
                            <FaMicrochip className="w-4 h-4 text-slate-500" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-gray-900 break-all">{device.serialNo || "Seri No Yok"}</p>
                          <p className="text-xs text-gray-500 truncate">{device.deviceInfo?.deviceName || device.deviceName || ""}</p>
                        </div>
                      </AppLink>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap shrink-0 ${device.isActive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${device.isActive ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
                        {deviceActivityStateLabel(device.isActive)}
                      </span>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      {(deviceDetailMode === "service" || deviceDetailMode === "playmaker") && daysInStatus != null && (
                        <span className={`text-sm font-extrabold ${deviceDetailMeta.text}`}>
                          {daysInStatus} <span className="text-xs font-bold">gündür {deviceDetailMeta.label.toLowerCase()}</span>
                        </span>
                      )}
                      {deviceDetailMode === "unused" && (
                        <span className={`text-sm font-extrabold ${deviceDetailMeta.text}`}>
                          {noConsDays != null ? (
                            <>{noConsDays} <span className="text-xs font-bold">gündür tüketim yok</span></>
                          ) : (
                            <span className="text-xs font-bold">
                              Hiç tüketim yok
                              {placedLabel(device) && (
                                <span className="ml-1.5 font-Medium text-gray-400">· {placedLabel(device)}</span>
                              )}
                            </span>
                          )}
                        </span>
                      )}
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold whitespace-nowrap ${isConnected ? "text-emerald-600" : "text-red-600"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
                        {isConnected
                          ? "Bağlı"
                          : offlineDays != null
                            ? `${offlineDays} gündür bağlı değil`
                            : "Hiç bağlanmadı"}
                      </span>
                      {deviceDetailMode === "playmaker" && device.playMaker && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-pink-50 text-pink-700 whitespace-nowrap">
                          <FaUserAlt className="w-2.5 h-2.5" />
                          {device.playMaker.name}{device.playMaker.lastName ? ` ${device.playMaker.lastName}` : ""}
                        </span>
                      )}
                    </div>

                    {deviceDetailMode === "critical" && crits.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {crits.map((p) => (
                          <span key={p.productName} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-red-50 text-red-700 border border-red-100 whitespace-nowrap">
                            {p.productName} <span className="font-bold">{p.quota}</span>/{p.quotaWarning}
                          </span>
                        ))}
                      </div>
                    )}

                    <AppLink
                      to={getFirmNavTo(devFirmId)}
                      state={getFirmNavState(devFirmId, device.firm)}
                      onClick={onClose}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-800"
                    >
                      <FaBuilding className="w-3 h-3 text-indigo-500" />
                      <span className="truncate">{device.firm?.firmName || "—"}</span>
                      {device.firm?.bayserNo && (
                        <span className="text-[11px] font-medium text-gray-400">Bayser: {device.firm.bayserNo}</span>
                      )}
                    </AppLink>
                    <ParentFirmBadge group={device.group} onNavigate={onClose} />
                  </div>
                );
              })
            : pageRows.map((row, idx) => {
                const firm = row.item;
                const fId = firm._id || firm.firmId;
                const tel = firm.tel || firm.phone;
                const addressText =
                  typeof firm.address === "string"
                    ? firm.address
                    : firm.address
                      ? [firm.address.text || firm.address.address, firm.address.town, firm.address.city].filter(Boolean).join(" / ")
                      : "";
                const productList = Array.isArray(firm.productInfo)
                  ? firm.productInfo
                  : firm.productInfo?.productInfo || [];
                const crits = productList.filter(
                  (p) => BASE_PRODUCTS.includes(p.productName) && p.quota < p.quotaWarning && p.quotaDistribution !== false
                );
                const orderItems = firm.products?.length ? firm.products : firm.daysSinceLastOrder != null ? [firm] : [];
                return (
                  <div key={fId || `f-${idx}`} className="px-4 py-3 space-y-2">
                    <AppLink
                      to={getFirmNavTo(fId)}
                      state={getFirmNavState(fId, firm)}
                      onClick={onClose}
                      className="flex items-center gap-2 min-w-0"
                    >
                      <div className="flex items-center justify-center shrink-0 w-9 h-9 rounded-lg bg-indigo-50 border border-indigo-100">
                        <FaBuilding className="w-4 h-4 text-indigo-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900 break-words leading-snug">{firm.firmName}</p>
                        {firm.bayserNo && (
                          <p className="text-[11px] font-medium text-gray-400">Bayser: {firm.bayserNo}</p>
                        )}
                      </div>
                    </AppLink>
                    <FirmContactsMenu contacts={firm.contacts} />
                    <ParentFirmBadge group={firm.group} onNavigate={onClose} />

                    {(tel || firm.email || addressText) && (
                      <div className="flex flex-col gap-0.5 text-xs text-gray-700">
                        {tel && (
                          <span className="inline-flex items-center gap-1.5"><FaPhone className="w-3 h-3 text-emerald-500 shrink-0" /> {tel}</span>
                        )}
                        {firm.email && (
                          <span className="inline-flex items-center gap-1.5 break-all"><FaEnvelope className="w-3 h-3 text-blue-500 shrink-0" /> {firm.email}</span>
                        )}
                        {addressText && (
                          <span className="inline-flex items-start gap-1.5"><FaMapMarkerAlt className="w-3 h-3 text-rose-500 shrink-0 mt-0.5" /> <span className="break-words">{addressText}</span></span>
                        )}
                      </div>
                    )}

                    {orderItems.length > 0 && (
                      <OrderResponsibleNote responsible={firm.responsible} onNavigate={onClose} />
                    )}

                    {/* Kompakt detay çipleri */}
                    <div className="flex flex-wrap gap-1.5">
                      {orderItems.map((product, pi) => (
                        <span key={`o-${pi}`} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-orange-50 text-orange-700 border border-orange-100 whitespace-nowrap">
                          <MdOutlineRemoveShoppingCart className="w-3 h-3" />
                          {product.productName || "Ürün"}:{" "}
                          <span className="font-bold">
                            {product.daysSinceLastOrder != null ? `${product.daysSinceLastOrder} gün` : "hiç sipariş yok"}
                          </span>
                          {product.orderedBy?.firmName && (
                            <span className="opacity-80">
                              · {product.orderedBy.firmName} {product.lastOrderSource === "parent" ? "faturası" : "aktardı"}
                            </span>
                          )}
                          <LastBillButton
                            size="sm"
                            product={product}
                            active={activeBillKey === billKey(firm, product)}
                            onClick={() => toggleBill(firm, product)}
                          />
                        </span>
                      ))}
                      {(firm.lowPerformanceProducts || []).map((product, pi) => (
                        <span key={`p-${pi}`} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-100 whitespace-nowrap">
                          <FaChartLine className="w-3 h-3" />
                          {product.productName}: <span className="font-bold">%{parseFloat(product.performanceRatio) || 0}</span>
                        </span>
                      ))}
                      {crits.map((p) => (
                        <span key={`c-${p.productName}`} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-red-50 text-red-700 border border-red-100 whitespace-nowrap">
                          <FaExclamationTriangle className="w-3 h-3 text-red-400" />
                          {p.productName} <span className="font-bold">{p.quota}</span>/{p.quotaWarning}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
        </div>

        <table className="hidden sm:table w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10">
            {alertDrawer.dataType === "device" ? (
              <tr className="border-b border-gray-200 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 shadow-sm">
                <th className="px-3 py-2.5 bg-gray-50">Cihaz</th>
                {deviceDetailMeta && <th className="px-3 py-2.5 bg-gray-50">{deviceDetailMeta.label}</th>}
                <th className="px-3 py-2.5 bg-gray-50">Bağlantı</th>
                <th className="px-3 py-2.5 bg-gray-50">Bileşenler</th>
                <th className="px-3 py-2.5 bg-gray-50">Firma</th>
              </tr>
            ) : (
              <tr className="border-b border-gray-200 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 shadow-sm">
                <th className={`px-3 py-2.5 bg-gray-50 ${billTarget ? "w-[38%]" : "w-[22%]"}`}>Firma</th>
                {!billTarget && <th className="px-3 py-2.5 bg-gray-50 w-[24%]">İletişim</th>}
                <th className="px-3 py-2.5 bg-gray-50">Detay</th>
              </tr>
            )}
          </thead>
          <tbody className="divide-y divide-gray-100">
            {alertDrawer.dataType === "device"
              ? pageRows.map((row, idx) => {
                  const device = row.item;
                  const devFirmId = device.firm?._id || device.firmId;
                  return (
                    <DeviceRow
                      key={device._id || idx}
                      device={device}
                      rowIdx={idx}
                      detailMode={deviceDetailMode}
                      deviceTo={`/Anasayfa/Cihazlar/${device._id}`}
                      deviceState={{ device, fromPage: "Anasayfa/Dashboard" }}
                      firmTo={getFirmNavTo(devFirmId)}
                      firmState={getFirmNavState(devFirmId, device.firm)}
                      onNavClick={onClose}
                    />
                  );
                })
              : pageRows.map((row, idx) => {
                  const firm = row.item;
                  const fId = firm._id || firm.firmId;
                  return (
                    <FirmRow
                      key={fId || `f-${idx}`}
                      firm={firm}
                      idx={idx}
                      compact={!!billTarget}
                      activeBillKey={activeBillKey}
                      onOpenBill={toggleBill}
                      firmTo={getFirmNavTo(fId)}
                      firmState={getFirmNavState(fId, firm)}
                      onNavClick={onClose}
                    />
                  );
                })}
          </tbody>
        </table>
        </div>
        {billTarget && (
          <LastOrderBillPanel
            key={activeBillKey}
            target={billTarget}
            onClose={() => setBillTarget(null)}
            className="w-full sm:w-1/2 xl:w-[45%] motion-safe:animate-panelSlideIn"
          />
        )}
      </Drawer.Body>

      <Drawer.Footer className="px-4 py-3 border-t border-gray-100 bg-white">
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500 font-medium">
            {listCount > 0
              ? `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, listCount)} / ${listCount}`
              : "0 / 0"}
          </p>
          <div className="flex items-center gap-1.5">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 transition-colors"
            >
              Önceki
            </button>
            <span className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg">
              {page}/{totalPages}
            </span>
            <button
              disabled={page >= totalPages || listCount === 0}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 transition-colors"
            >
              Sonraki
            </button>
          </div>
        </div>
      </Drawer.Footer>
    </Drawer>
  );
}

function DeviceRow({ device, rowIdx, detailMode = null, deviceTo, deviceState, firmTo, firmState, onNavClick }) {
  const detailMeta = detailMode ? DEVICE_DETAIL_META[detailMode] : null;
  const isConnected = device.deviceInfo?.connection?.status;
  const lastConn = device.deviceInfo?.connection?.lastConnected || device.lastConnectionDate;
  const hasGrinder =
    getGrinderStatus(device).label !== "-" ||
    (device.componentSummary?.grinderStatus && device.componentSummary.grinderStatus !== "-");
  const daysInStatus = device.statusChangedAt
    ? Math.floor((Date.now() - new Date(device.statusChangedAt).getTime()) / 86400000)
    : null;
  const noConsDays = device.lastConsumptionDate
    ? Math.floor((Date.now() - new Date(device.lastConsumptionDate).getTime()) / 86400000)
    : null;
  const criticals = (device.productInfo?.productInfo || []).filter(
    (p) => p.quotaDistribution && p.quota < p.quotaWarning
  );
  const firmName = device.firm?.firmName || "—";

  return (
    <tr className={`align-top transition-colors ${rowIdx % 2 === 1 ? "bg-gray-50/60" : "bg-white"} hover:bg-indigo-50/40`}>
      {/* Cihaz — ikon + seri + model tek link (group-hover);
          altında Açık/Kapalı rozeti (liste sayfalarıyla aynı desen) */}
      <td className="px-3 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="min-w-0">
            <AppLink
              to={deviceTo}
              state={deviceState}
              onClick={onNavClick}
              className="group flex items-center gap-2 min-w-0 w-fit max-w-full"
              title="Cihaza git"
            >
              <div className="w-10 h-10 shrink-0 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden border border-transparent transition-colors group-hover:bg-indigo-50 group-hover:border-indigo-300">
                {(() => {
                  const devImg = DEVICE_IMAGES[device.deviceInfo?.deviceName || device.deviceName];
                  return devImg ? (
                    <img src={devImg} alt={device.deviceInfo?.deviceName || device.deviceName} className="max-w-full max-h-full object-contain p-0.5" />
                  ) : (
                    <FaMicrochip className="w-4 h-4 text-slate-500 transition-colors group-hover:text-indigo-600" />
                  );
                })()}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-gray-900 break-all transition-colors group-hover:text-indigo-700 group-hover:underline underline-offset-2 decoration-indigo-400">
                  {device.serialNo || "Seri No Yok"}
                </div>
                <div className="text-xs font-medium text-gray-600 truncate max-w-[150px] transition-colors group-hover:text-indigo-500 group-hover:underline underline-offset-2 decoration-indigo-300">
                  {device.deviceInfo?.deviceName || device.deviceName || "İsimsiz Cihaz"}
                </div>
              </div>
            </AppLink>
            <span
              className={`mt-1 inline-flex w-fit items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap ${
                device.isActive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${device.isActive ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
              {deviceActivityStateLabel(device.isActive)}
            </span>
            {/* Detay sütunu olan kategorilerde bilgiler kendi sütununda;
                burada yalnızca sütunda gösterilmeyen kalanlar */}
            {((detailMode !== "service" && detailMode !== "playmaker" && daysInStatus != null) ||
              (detailMode !== "unused" && noConsDays != null) ||
              (detailMode !== "critical" && criticals.length > 0)) && (
              <div className="flex flex-wrap gap-1 mt-1">
                {detailMode !== "service" && detailMode !== "playmaker" && daysInStatus != null && (
                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-100 whitespace-nowrap">
                    <FaClock className="w-2.5 h-2.5 shrink-0" /> {daysInStatus} gündür
                  </span>
                )}
                {detailMode !== "unused" && noConsDays != null && (
                  <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200 whitespace-nowrap">
                    <FaChartLine className="w-2.5 h-2.5 shrink-0" /> {noConsDays} gündür tüketim yok
                  </span>
                )}
                {detailMode !== "critical" &&
                  criticals.map((p) => (
                    <span key={p.productName} className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md bg-red-50 text-red-700 border border-red-100 whitespace-nowrap">
                      <FaExclamationTriangle className="w-2.5 h-2.5 text-red-400 shrink-0" />
                      {p.productName} <span className="text-red-400 font-normal">{p.quota}/{p.quotaWarning}</span>
                    </span>
                  ))}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Kategoriye özel detay sütunu — büyük yazım (DeviceStatusDrawer deseni) */}
      {detailMeta && (
        <td className="px-3 py-3">
          {(() => {
            if (detailMode === "service" || detailMode === "playmaker") {
              return (
                <div className="flex flex-col gap-1 whitespace-nowrap">
                  {daysInStatus != null ? (
                    <div className="flex items-baseline gap-1.5">
                      <span className={`text-lg font-extrabold leading-none tabular-nums ${detailMeta.text}`}>{daysInStatus}</span>
                      <span className={`text-xs font-bold ${detailMeta.text}`}>
                        gündür {detailMeta.label.toLowerCase()}
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                  {device.statusChangedAt && (
                    <span className="text-[11px] text-gray-400">
                      {new Date(device.statusChangedAt).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  )}
                  {detailMode === "playmaker" && device.playMaker && (
                    <span className="inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-pink-50 text-pink-700 whitespace-nowrap">
                      <FaUserAlt className="w-3 h-3" />
                      {device.playMaker.name}{device.playMaker.lastName ? ` ${device.playMaker.lastName}` : ""}
                    </span>
                  )}
                </div>
              );
            }
            if (detailMode === "unused") {
              return (
                <div className="flex flex-col gap-1 whitespace-nowrap">
                  {noConsDays != null ? (
                    <div className="flex items-baseline gap-1.5">
                      <span className={`text-lg font-extrabold leading-none tabular-nums ${detailMeta.text}`}>{noConsDays}</span>
                      <span className={`text-xs font-bold ${detailMeta.text}`}>gündür tüketim yok</span>
                    </div>
                  ) : (
                    <>
                      <span className={`text-xs font-bold ${detailMeta.text}`}>Hiç tüketim yok</span>
                      {placedLabel(device) && (
                        <span className="text-[11px] text-gray-400">{placedLabel(device)}</span>
                      )}
                    </>
                  )}
                  {device.lastConsumptionDate && (
                    <span className="text-[11px] text-gray-400">
                      Son: {new Date(device.lastConsumptionDate).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  )}
                </div>
              );
            }
            if (detailMode === "critical") {
              if (criticals.length === 0) return <span className="text-sm text-gray-400">—</span>;
              return (
                <div className="flex flex-col gap-1.5">
                  {criticals.map((p) => (
                    <span key={p.productName} className="inline-flex w-fit items-baseline gap-1.5 px-2 py-1 rounded-lg bg-red-50 border border-red-100 whitespace-nowrap">
                      <span className="text-xs font-semibold text-gray-700">{p.productName}</span>
                      <span className="text-sm font-extrabold leading-none text-red-600 tabular-nums">{p.quota}</span>
                      <span className="text-[11px] font-medium text-red-400">/ {p.quotaWarning}</span>
                    </span>
                  ))}
                </div>
              );
            }
            return null;
          })()}
        </td>
      )}

      {/* Bağlantı — bağlı değilse kaç gündür bağlanmadığı büyük yazılır */}
      <td className="px-3 py-3">
        {isConnected ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-md border whitespace-nowrap bg-emerald-50 text-emerald-700 border-emerald-100">
            <FaCircle className="h-2 w-2 shrink-0 text-emerald-500" />
            Bağlı
          </span>
        ) : (
          (() => {
            const offlineDays = lastConn
              ? Math.floor((Date.now() - new Date(lastConn).getTime()) / 86400000)
              : null;
            return (
              <div className="flex flex-col gap-1 whitespace-nowrap">
                {offlineDays != null ? (
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-lg font-extrabold leading-none tabular-nums text-red-600">{offlineDays}</span>
                    <span className="text-xs font-bold text-red-600">gündür bağlı değil</span>
                  </div>
                ) : (
                  <span className="text-xs font-bold text-red-600">Hiç bağlanmadı</span>
                )}
                {lastConn && (
                  <span className="text-[11px] text-gray-400">
                    Son: {new Date(lastConn).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}
                  </span>
                )}
              </div>
            );
          })()
        )}
      </td>

      {/* Bileşenler */}
      <td className="px-3 py-3">
        <div className="flex flex-wrap gap-1.5">
          <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-slate-50 text-slate-700 border border-slate-200 whitespace-nowrap">
            <FaCoffee className="w-2.5 h-2.5 shrink-0" /> {getDeviceCupNumber(device)} göz
          </span>
          {hasGrinder && (
            <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-100 whitespace-nowrap">
              <FaCogs className="w-2.5 h-2.5 shrink-0" /> Değirmenli
            </span>
          )}
        </div>
      </td>

      {/* Firma */}
      <td className="px-3 py-3">
        <div className="flex flex-col items-start gap-1">
          <AppLink
            to={firmTo}
            state={firmState}
            onClick={onNavClick}
            className="group flex items-center gap-2 min-w-0 w-fit max-w-full"
            title="Firmaya git"
          >
            <div className="flex items-center justify-center shrink-0 w-7 h-7 rounded-lg bg-indigo-50 border border-indigo-100 transition-colors group-hover:bg-indigo-100 group-hover:border-indigo-300">
              <FaBuilding className="w-3.5 h-3.5 text-indigo-500 transition-colors group-hover:text-indigo-700" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-xs font-semibold text-gray-900 truncate max-w-[130px] transition-colors group-hover:text-indigo-700 group-hover:underline underline-offset-2 decoration-indigo-400">
                {firmName}
              </span>
              {device.firm?.bayserNo && (
                <span className="text-[11px] font-medium text-gray-400 truncate transition-colors group-hover:text-indigo-500 group-hover:underline underline-offset-2 decoration-indigo-300">
                  Bayser: {device.firm.bayserNo}
                </span>
              )}
            </div>
          </AppLink>
          <ParentFirmBadge group={device.group} onNavigate={onNavClick} />
          {/* Plasiyer kategorisinde plasiyer adı detay sütununda gösterilir */}
          {detailMode !== "playmaker" && device.playMaker && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-pink-50 text-pink-700 whitespace-nowrap">
              <FaUserAlt className="w-2.5 h-2.5" />
              {device.playMaker.name}{device.playMaker.lastName ? ` ${device.playMaker.lastName}` : ""}
            </span>
          )}
        </div>
      </td>

    </tr>
  );
}

function FirmRow({ firm, idx, firmTo, firmState, onNavClick, compact = false, activeBillKey = null, onOpenBill }) {
  const tel = firm.tel || firm.phone;
  // address string veya {city, town, ...} objesi olabilir
  const addressText =
    typeof firm.address === "string"
      ? firm.address
      : firm.address
        ? [firm.address.text || firm.address.address, firm.address.town, firm.address.city].filter(Boolean).join(" / ")
        : "";

  return (
    <tr className={`align-top transition-colors ${idx % 2 === 1 ? "bg-gray-50/60" : "bg-white"} hover:bg-indigo-50/40`}>
      {/* Firma — ikon + isim + bayser tek link (diğer listelerle aynı desen) */}
      <td className="px-3 py-3">
        <AppLink
          to={firmTo}
          state={firmState}
          onClick={onNavClick}
          className="group flex items-center gap-2 min-w-0 w-fit max-w-full"
          title="Firmaya git"
        >
          <div className="flex items-center justify-center shrink-0 w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 transition-colors group-hover:bg-indigo-100 group-hover:border-indigo-300">
            <FaBuilding className="w-4 h-4 text-indigo-500 transition-colors group-hover:text-indigo-700" />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-gray-900 break-words leading-snug transition-colors group-hover:text-indigo-700 group-hover:underline underline-offset-2 decoration-indigo-400">
              {firm.firmName}
            </span>
            {firm.bayserNo && (
              <span className="text-[11px] font-medium text-gray-400 truncate transition-colors group-hover:text-indigo-500 group-hover:underline underline-offset-2 decoration-indigo-300">
                Bayser: {firm.bayserNo}
              </span>
            )}
          </div>
        </AppLink>
        {/* Muhataplar (#367) — ilk muhatap; fazlası satırı itmeyen açılır listede */}
        <FirmContactsMenu contacts={firm.contacts} className="mt-1" />
        <ParentFirmBadge group={firm.group} className="mt-1.5" onNavigate={onNavClick} />
      </td>
      {/* İletişim — tüm firma kategorilerinde (telefon + e-posta + adres).
          Fatura paneli açıkken tablo daraldığı için gizlenir (#367). */}
      <td className={`px-3 py-3 align-top ${compact ? "hidden" : ""}`}>
        <div className="flex flex-col gap-1 text-xs text-gray-700">
          {tel && (
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
              <FaPhone className="w-3 h-3 text-emerald-500 shrink-0" /> {tel}
            </span>
          )}
          {firm.email && (
            <span className="inline-flex items-center gap-1.5 break-all">
              <FaEnvelope className="w-3 h-3 text-blue-500 shrink-0" /> {firm.email}
            </span>
          )}
          {addressText && (
            <span className="inline-flex items-start gap-1.5">
              <FaMapMarkerAlt className="w-3 h-3 text-rose-500 shrink-0 mt-0.5" />
              <span className="break-words">{addressText}</span>
            </span>
          )}
          {!tel && !firm.email && !addressText && <span className="text-gray-400">—</span>}
        </div>
      </td>
      {/* Detay */}
      <td className="px-3 py-3">
        <div className="min-w-0">
            {firm.lowPerformanceProducts && (
              <div className="mt-2.5 space-y-2">
                {firm.lowPerformanceProducts.map((product, pi) => {
                  const ratio = parseFloat(product.performanceRatio) || 0;
                  const bgColor = ratio < 30 ? "bg-red-50" : ratio < 50 ? "bg-orange-50" : "bg-amber-50";
                  const barColor = ratio < 30 ? "bg-red-500" : ratio < 50 ? "bg-orange-500" : "bg-amber-500";
                  const textColor = ratio < 30 ? "text-red-600" : ratio < 50 ? "text-orange-600" : "text-amber-600";
                  const borderColor = ratio < 30 ? "border-red-200" : ratio < 50 ? "border-orange-200" : "border-amber-200";
                  const iconColor = ratio < 30 ? "text-red-400" : ratio < 50 ? "text-orange-400" : "text-amber-400";
                  return (
                    <div key={pi} className={`rounded-xl p-3 ${bgColor} border ${borderColor} w-full`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <FaBox className={`w-3.5 h-3.5 shrink-0 ${iconColor}`} />
                          <span className="text-sm font-bold text-gray-800">{product.productName}</span>
                        </div>
                        <span className={`text-sm font-extrabold ${textColor}`}>%{ratio}</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
                        <div className={`h-full rounded-full ${barColor} transition-all duration-500`} style={{ width: `${Math.min(ratio, 100)}%` }} />
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                        <span className="flex items-center gap-1 text-gray-600">
                          <FaChartLine className="w-3 h-3 text-gray-400" />
                          <span className="font-bold text-gray-800">{product.totalConsumption || 0}</span>
                          <span className="text-gray-400">/ {product.totalGoalConsumption != null ? Math.floor(product.totalGoalConsumption) : "-"}</span>
                        </span>
                        <span className="text-gray-300">|</span>
                        <span className="flex items-center gap-1 text-gray-600">
                          <FaCalendarAlt className="w-3 h-3 text-gray-400" />
                          Aylık: <span className="font-bold text-gray-800">{product.goalConsumentMonthly || "-"}</span>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Sipariş vermeyen ürünler — ürün başına TEK SATIR (#367, sade):
                ürün · gün / aralık (ya da hiç sipariş yok) · son sipariş · fatura.
                Hatırlatma yalnız üst firma varsa (firmanın kendisi zaten listede) */}
            {(firm.products?.length || firm.daysSinceLastOrder != null) && (
              <OrderResponsibleNote className="mt-2" responsible={firm.responsible} onNavigate={onNavClick} />
            )}
            {(firm.products?.length || firm.daysSinceLastOrder != null) && (
              <ul className="mt-2 divide-y divide-orange-100 rounded-xl border border-orange-200 bg-orange-50/50">
                {(firm.products?.length ? firm.products : [firm]).map((product, pi) => (
                  <li key={pi} className="flex items-center gap-3 px-3 py-2">
                    <span className="flex min-w-0 flex-1 items-center gap-1.5 text-sm font-bold text-gray-800">
                      <FaBox className="h-3.5 w-3.5 shrink-0 text-orange-400" />
                      <span className="truncate">{product.productName || "Ürün"}</span>
                    </span>
                    {product.daysSinceLastOrder != null ? (
                      <span
                        className="flex shrink-0 items-baseline gap-1 tabular-nums"
                        title={product.orderFrequencyDays != null ? `Sipariş aralığı ${product.orderFrequencyDays} gün` : undefined}
                      >
                        <span className="text-lg font-extrabold leading-none text-orange-600">{product.daysSinceLastOrder}</span>
                        {product.orderFrequencyDays != null && (
                          <span className="text-xs font-bold text-orange-300">/ {product.orderFrequencyDays}</span>
                        )}
                        <span className="text-xs font-bold text-orange-600">gün</span>
                      </span>
                    ) : (
                      <span className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-red-100 px-2 py-0.5 text-xs font-bold text-red-700">
                        <MdOutlineRemoveShoppingCart className="h-3 w-3" /> Hiç sipariş yok
                      </span>
                    )}
                    {product.lastOrderDate && (
                      <span
                        className="hidden shrink-0 text-xs text-gray-500 md:inline"
                        title={
                          product.orderedBy?.firmName
                            ? `${product.orderedBy.firmName} ${product.lastOrderSource === "parent" ? "faturası" : "aktardı"}`
                            : "Son sipariş"
                        }
                      >
                        Son {new Date(product.lastOrderDate).toLocaleDateString("tr-TR")}
                      </span>
                    )}
                    {/* Son sipariş faturası — tablo sola daralır, sağda detay (#367) */}
                    <LastBillButton
                      size="sm"
                      product={product}
                      active={activeBillKey === billKey(firm, product)}
                      onClick={() => onOpenBill?.(firm, product)}
                    />
                  </li>
                ))}
              </ul>
            )}

            {(() => {
                // Kritik kota payload'ında firm.productInfo bazen doğrudan
                // ÜRÜN DİZİSİ (server firm.productInfo = firmProductInfo
                // ataması), bazen populate edilmiş { productInfo: [...] }
                // dokümanıdır — iki şekli de destekle.
                const productList = Array.isArray(firm.productInfo)
                  ? firm.productInfo
                  : firm.productInfo?.productInfo || [];
                const criticals = productList.filter((p) => BASE_PRODUCTS.includes(p.productName) && p.quota < p.quotaWarning && p.quotaDistribution !== false);
                if (criticals.length === 0) return null;
                return (
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    {criticals.map((p) => {
                      const ratio = p.quotaWarning > 0 ? Math.round((p.quota / p.quotaWarning) * 100) : 0;
                      return (
                        <div key={p.productName} className="bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-1.5">
                              <FaExclamationTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                              <span className="text-sm font-bold text-gray-800">{p.productName}</span>
                            </div>
                            <span className="text-xs font-extrabold text-red-600">%{ratio}</span>
                          </div>
                          <div className="w-full h-1.5 bg-red-200 rounded-full overflow-hidden mb-1.5">
                            <div className="h-full rounded-full bg-red-500" style={{ width: `${Math.min(ratio, 100)}%` }} />
                          </div>
                          <span className="flex items-center gap-1 text-xs text-gray-600">
                            <FaBox className="w-3 h-3 text-red-300" />
                            Kota: <span className="font-bold text-red-700">{p.quota}</span>
                            <span className="text-gray-400">/ {p.quotaWarning}</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
        </div>
      </td>
    </tr>
  );
}
